import { useSendAuthCode } from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, TextInput as RNTextInput, View } from 'react-native';
import { InferType, string, object as yupObject } from 'yup';
import { Button } from '../../components/crop-circle/Button';
import { colors, typography } from '../../components/crop-circle/tokens';
import { Colors } from '../../themes/colors';
import { LoginProps } from './props';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

const formSchema = yupObject({
  email: string().required().email().lowercase(),
});

type FormData = InferType<typeof formSchema>;

export default function EmailCodeLogin(props: LoginProps) {
  const { t } = useTranslation();
  const { sendAuthCodeAsync, isPending: isLoading } = useSendAuthCode();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    const input = { email: data.email };
    const handleCodeSent = props.onCodeSent ?? (() => null);
    await sendAuthCodeAsync(input, handleCodeSent, props.onRegister);
  };

  return (
    <View style={styles.wrapper}>
      <Controller
        control={control}
        name="email"
        render={({ field: { onChange, onBlur, value } }) => (
          <View style={styles.inputContainer}>
            <RNTextInput
              value={value || ''}
              onChangeText={(text) => onChange(text.toLowerCase())}
              onBlur={onBlur}
              placeholder="user@email.com"
              keyboardType="email-address"
              autoComplete="email"
              autoCapitalize="none"
              placeholderTextColor={Colors.role.textDisabled}
              style={[
                styles.input,
                Platform.OS === 'web' && ({ outlineStyle: 'none' } as any),
              ]}
            />
          </View>
        )}
      />
      <Button
        variant="rectangular"
        size="large"
        state={isValid && !isLoading ? 'active' : 'default'}
        loading={isLoading}
        onPress={handleSubmit(onSubmit)}
        style={styles.button}
      >
        {t('login.continue')}
      </Button>
    </View>
  );
}

const styles = themedStyleSheet((role) => ({
  wrapper: {
    alignSelf: 'stretch',
    gap: 24,
  },
  inputContainer: {
    height: 48,
    backgroundColor: role.surfaceHover,
    borderRadius: 4,
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  input: {
    ...typography.body.small,
    color: role.textPrimary,
    backgroundColor: 'transparent',
  },
  button: {
    width: '100%',
  },
}));
